import React, { MouseEvent, ReactElement, useState } from 'react';
import { useQueryClient } from 'react-query';

import ConfirmModal from '../Common/Modal/ConfirmModal';
import apiManager from '@/api/AxiosInstance';
import newApiManager from '@/api/NewAxiosInstance';
import { COMMON_TOAST_ERROR } from '@/const/errorMessage';
import { AFTER_CONSULTING, BEFORE_CONSULTING, BEFORE_SALE, PAUSE, SALE } from '@/const/productStatus';
import { PRODUCT_TYPE_DIRECT, PRODUCT_TYPE_SPEED } from '@/const/products';
import { useToastContext } from '@/contexts/Common/ToastContext';
import { useMemberContext } from '@/contexts/Member/MemberContext';

interface ProductTypeAndStatusProps {
  id: number;
  type: EnumPresenter | undefined;
  status: EnumPresenter | undefined;
  setProductList?: React.Dispatch<React.SetStateAction<ProductDetailResponse[]>>;
  statusOfSpeedProduct?: EnumPresenter;
}

const TYPE_CHANGE_BUTTON_CLASS =
  'mt-1 flex items-center justify-center w-[173px] h-[28px] rounded-[6px] border border-primary-7 text-primary-7 text-[13px] font-semibold';

const getButtonLabel = (code: string | undefined) => {
  if (code == PRODUCT_TYPE_DIRECT) {
    return '직트럭 매입 신청하기';
  } else if (code == PRODUCT_TYPE_SPEED) {
    return '직거래 판매로 변경';
  } else {
    return '';
  }
};

export default function ProductTypeChangeButton({
  id,
  type,
  status,
  setProductList,
  statusOfSpeedProduct,
}: ProductTypeAndStatusProps) {
  const [isShow, setIsShow] = useState(false);
  const [modalContent, setModalContent] = useState<ReactElement>();
  const [rightBtnText, setRightBtnText] = useState('');
  const queryClient = useQueryClient();
  const { showToast } = useToastContext();
  const { member } = useMemberContext();

  const onClickLeftBtn = (e?: MouseEvent<HTMLDivElement>) => {
    setIsShow(false);
    if (e) {
      e.stopPropagation();
    }
  };

  const handleToastMsg = (requestType: string) => {
    if (requestType == PRODUCT_TYPE_DIRECT) {
      return '직거래 차량으로 변경되었어요.';
    } else {
      return '직트럭에 즉시 매각을 요청했어요.';
    }
  };

  const onClickRightBtn = (type: string | undefined, e?: MouseEvent<HTMLDivElement>) => {
    let requestType = '';
    if (type == PRODUCT_TYPE_DIRECT) {
      requestType = PRODUCT_TYPE_SPEED;
    } else {
      requestType = PRODUCT_TYPE_DIRECT;
    }

    const payload =
      requestType === PRODUCT_TYPE_SPEED
        ? { type: requestType, status: PAUSE }
        : { type: requestType };

    newApiManager
      .patch(`/api/v1/products/${id}`, payload)
      .then((response) => {
        if (setProductList) {
          const responseData: ProductDetailResponse = response.data;
          setProductList((prevData) =>
            prevData.map((item) => {
              if (item.id === responseData.id) {
                return {
                  ...item,
                  type: responseData.type,
                  status:
                    requestType === PRODUCT_TYPE_SPEED
                      ? responseData.status?.code === PAUSE
                        ? responseData.status
                        : { code: PAUSE, desc: '판매중지' }
                      : responseData.status ?? item.status,
                };
              }
              return item;
            }),
          );
        }
        queryClient.invalidateQueries({ queryKey: ['product-search'] });
        const newToastMsg = handleToastMsg(requestType);
        showToast(newToastMsg, 'success', 'bottom');
      })
      .catch((error) => {
        showToast(COMMON_TOAST_ERROR, 'error', 'bottom');
      });

    if (e) {
      e.stopPropagation();
    }

    setIsShow(false);
  };

  const getTextContext = () => {
    if (type?.code == PRODUCT_TYPE_DIRECT) {
      setModalContent(
        <>
          <span className="font-semibold text-[20px] sm280:text-[16px]">직트럭에 즉시 매각 요청할까요?</span>
          <br />
          <br />
          <span className="text-[18px] sm280:text-[14px] break-keep">
            · 직거래 차량은{' '}
            <span className="text-primary-10">
              내차구매 목록에서 <br />
              숨김 처리
            </span>
            됩니다.
            <br />· 상담 종료 시{' '}
            <span className="text-primary-10">
              내차구매 목록에 자동 <br />
              노출
            </span>
            됩니다.
          </span>
        </>,
      );
      setRightBtnText('요청하기');
    } else {
      setModalContent(
        <span>
          직거래 차량으로
          <br />
          변경하시겠어요?
        </span>,
      );

      setRightBtnText('변경하기');
    }
  };

  const buttonLabel = getButtonLabel(type?.code);

  return (
    member?.type?.code !== 'DEALER' &&
    (statusOfSpeedProduct?.code === AFTER_CONSULTING || statusOfSpeedProduct?.code === BEFORE_CONSULTING) &&
    ((status?.code === SALE && type?.code === PRODUCT_TYPE_DIRECT) || status?.code === BEFORE_SALE) && (
      <>
        {buttonLabel && (
          <button
            type="button"
            className={TYPE_CHANGE_BUTTON_CLASS}
            onClick={(e) => {
              getTextContext();
              setIsShow(true);
              e.stopPropagation();
            }}
          >
            {buttonLabel}
          </button>
        )}

        <ConfirmModal
          isOpen={isShow}
          content={modalContent}
          onLeftButtonClick={(e?: MouseEvent<HTMLDivElement>) => onClickLeftBtn(e)}
          rightButtonLabel={rightBtnText}
          onRightButtonClick={(e?: MouseEvent<HTMLDivElement>) => onClickRightBtn(type?.code, e)}
        />
      </>
    )
  );
}
